import React, { useState, useEffect } from 'react';
import { formatMoney, unformat } from 'accounting-js';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Link from '../../utils/link';
import Typography from '../../atoms/typography';

import { useProduct } from '../../utils/context/product-provider';

const Brand = styled(Typography)`
  color: ${(props) => props.theme.color.brandLight};
  cursor: pointer;
  margin-bottom: 0.2em;
`;

const Title = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
`;

const Price = styled(Typography)`
  color: ${({ theme }) => theme.color.brand};
  justify-content: center;
  letter-spacing: normal;
  line-height: 2.2rem;
  margin-top: 1rem;
  text-transform: uppercase;
`;

const NoUnderlineAnchor = styled.a`
  text-decoration: none;
`;

const Header = styled.header`
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  align-content: center;

  ${Brand}, ${Title}, ${Price} {
    line-height: 1;
    text-align: center;
  }
`;

const OptionInvalid = styled.span`
  color: ${(props) => props.theme.color.brandError};
`;

const PayPalMessage = styled.div`
  margin-top: ${(props) => props.theme.modularScale.medium};
`;

const ProductHeader = (props) => {
  const { state: { addedOptionalMonogram } } = useProduct();

  const getVendorPrices = () => props.variant.prices.filter((v) => v.vendor_id === props.vendor);

  const getPrice = () => {
    const priceArray = props.variant ? getVendorPrices() : props.prices;

    const prices = priceArray.map(
      (p) => ({ original_price: p?.original_price ?? 0, price: p?.price ?? 0 })
    );
    const sales = priceArray.filter((p) => p?.on_sale || false);

    const final = { has_sale: false, discount: null };

    const getMin = (arr) => arr.reduce((acc, p) => {
      const a = acc;

      if (+p.price < +a.price && +p.price > 0) {
        a.original_price = +p.original_price;
        a.price = +p.price;
      }

      return a;
    }, {
      original_price: Math.min.apply(null, arr.map((p) => p.original_price).filter(Boolean)),
      price: Math.min.apply(null, arr.map((p) => p.price).filter(Boolean))
    });

    const getMax = (arr) => arr.reduce((acc, p) => {
      const a = acc;

      if (+p.price > +a.price) {
        a.original_price = +p.original_price;
        a.price = +p.price;
      }

      return a;
    }, { original_price: +arr[0].original_price, price: +arr[0].price });

    if (sales.length > 0) {
      const percents = sales.map((s) => Math.round(s.discount_percent));

      if (percents.every((p) => p === percents[0])) {
        final.discount = `${Math.round(percents[0])}% Off`;
      } else {
        final.discount = `Up to ${Math.max(...percents)}% Off`;
      }

      final.has_sale = true;
      final.max = getMax(sales);
      final.min = getMin(sales);
    } else {
      final.min = prices.length ? getMin(prices) : { price: 0 };
      final.max = prices.length ? getMax(prices) : { price: 0 };
    }

    if (props.variant && props.variant?.prices[0]?.monogram) {
      const { monogram } = props.variant?.prices[0];

      // if the current variant has
      // optional monogram with
      // an additional cost
      if (monogram.monogrammable
        && !monogram.monogrammable_only
        && (+monogram.monogram_price !== 0)) {
        final.monogram_price = +monogram.monogram_price;
      }
    }

    return final;
  };

  const [currentPrice, setCurrentPrice] = useState(getPrice());

  const addMonogramPrice = (priceObj, monogramCost) => {
    const final = {};

    final.original_price = priceObj.original_price + monogramCost;
    final.price = priceObj.original_price + monogramCost;

    return final;
  };

  useEffect(() => {
    if (props.variant) setCurrentPrice(getPrice());
  }, [props.variant]);

  useEffect(() => {
    if (addedOptionalMonogram && currentPrice.monogram_price) {
      const withMonogramPrice = currentPrice;

      withMonogramPrice.max = addMonogramPrice(currentPrice.max, currentPrice.monogram_price);
      withMonogramPrice.min = addMonogramPrice(currentPrice.min, currentPrice.monogram_price);

      setCurrentPrice(withMonogramPrice);
    } else {
      setCurrentPrice(getPrice());
    }
  }, [addedOptionalMonogram]);

  return (
    <Header>
      {
        props.brand && (
          <>
            {
              props.brandSlug ? (
                <Link href={`/brands/${props.brandSlug}`} passHref>
                  <NoUnderlineAnchor>
                    <Brand element="h2" like="heading-3">{props.brand}</Brand>
                  </NoUnderlineAnchor>
                </Link>
              ) : (
                <Brand element="h2" like="heading-3">{props.brand}</Brand>
              )
            }
          </>
        )
      }

      <Link
        href={`/product/${props.slug}`}
        passHref
      >
        <NoUnderlineAnchor>
          <Title element="h1" like="heading-3">{props.title}</Title>
        </NoUnderlineAnchor>
      </Link>

      <Price tabIndex="0" element="div" like="dec-1">
        {
          props.noOffers || currentPrice.min.price + currentPrice.max.price === 0
            ? <OptionInvalid>Out of Stock</OptionInvalid>
            : (
              <>
                {currentPrice.has_sale && (
                  <>
                    {currentPrice.min.price !== currentPrice.max.price && 'From '}
                    <Price
                      element="span"
                      like="label-1"
                      css={(theme) => ({
                        textDecoration: 'line-through',
                        color: theme.color.brand
                      })}
                    >
                      {formatMoney(currentPrice.min.original_price)}

                    </Price>
                    {' '}
                    {formatMoney(currentPrice.min.price)}
                    {' | '}
                    <Price
                      element="span"
                      like="label-1"
                      css={(theme) => ({
                        color: theme.color.brandA11yRed
                      })}
                    >
                      {currentPrice.discount}

                    </Price>
                  </>
                )}

                {!currentPrice.has_sale && (
                  <>
                    {
                      +currentPrice.min.price === +currentPrice.max.price
                        ? formatMoney(currentPrice.min.price)
                        : `${formatMoney(currentPrice.min.price)} - ${formatMoney(currentPrice.max.price)}`
                    }
                  </>
                )}
              </>
            )
        }
      </Price>

      <PayPalMessage
        data-pp-message
        data-pp-style-layout="text"
        data-pp-style-logo-type="inline"
        data-pp-style-text-color="black"
        data-pp-style-text-align="center"
        data-pp-amount={unformat(+currentPrice.min.price)}
      />
    </Header>
  );
};

ProductHeader.defaultProps = {
  title: '',
  brand: '',
  brandSlug: '',
  slug: '',
  variant: false,
  vendor: 0,
  noOffers: false
};

ProductHeader.propTypes = {
  brand: PropTypes.string,
  brandSlug: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  prices: PropTypes.array.isRequired,
  variant: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  vendor: PropTypes.number,
  noOffers: PropTypes.bool
};

ProductHeader.whyDidYouRender = true;

export default ProductHeader;
